import { PROTOCOL_ID_TN_STR } from '@/src/utils/constants';
import { type PaymentKeyHash, type Script } from '@lucid-evolution/lucid';
import 'reflect-metadata';
import {
    AssetClass,
    asSmartDBEntity,
    Convertible,
    FundCategory,
    FundFactory,
    isMainnet,
    isNullOrBlank,
    MinMaxDef,
    OptionsGet,
    optionsGetMinimal,
    type CS,
    type PaymentPubKey,
    type POSIXTime,
} from '../Commons';
import { deserealizeFundCategory, fundCategoryfromPlutusData } from '../Commons/conversions';
import { FundEntity } from './Fund.Entity';
import { BaseSmartDBEntity, SmartUTxOEntity } from './index.exports';

@asSmartDBEntity()
export class ProtocolEntity extends BaseSmartDBEntity {
    protected static _apiRoute: string = 'protocols';
    protected static _className: string = 'Protocol';
    // protected static _mongoTableName: string = 'Protocol';

    protected static _plutusDataIndex = 0;
    protected static _isOnlyDatum = false;

    // #region fields

    _NET_id_TN_Str: string = PROTOCOL_ID_TN_STR;

    _isDeployed = false;

    // sobre escribo este campo del base smart db, por que alli no se carga y para el fondo es necesario cargarlo para ver si el fondo esta deployado o no
    @Convertible({ type: String, propertyToFill: 'smartUTxO', relation: 'OneToOne', typeRelation: SmartUTxOEntity, cascadeSave: true, cascadeLoad: true })
    smartUTxO_id!: string | undefined;

    @Convertible({ propertyToFill: 'Funds', relation: 'OneToMany', typeRelation: FundEntity, cascadeSave: false, cascadeLoad: false })
    funds_ids!: string[];
    Funds!: FundEntity[];

    @Convertible({ isUnique: true })
    name!: string;

    @Convertible({})
    pdpProtocolVersion!: number;

    @Convertible({})
    pdpSwapOfferVersion!: number;

    @Convertible({})
    pdpBuyOrderVersion!: number;

    @Convertible({})
    pdpDelegationVersion!: number;

    @Convertible({})
    pdpScriptVersion!: number;

    @Convertible({ isUnique: true })
    pdpProtocolPolicyID_CS!: CS;

    @Convertible()
    pdpProtocolPolicyID_Script!: Script;

    @Convertible()
    pdpProtocolPolicyID_Params!: object;

    @Convertible()
    pdpProtocolValidator_AddressMainnet!: string;

    @Convertible()
    pdpProtocolValidator_AddressTestnet!: string;

    @Convertible()
    pdpProtocolValidator_Script!: Script;

    @Convertible({ isUnique: true })
    pdpProtocolValidator_Hash!: string;

    @Convertible()
    pdpProtocolValidator_Params!: object;

    @Convertible()
    pdpScriptPolicyID_CS!: CS;

    @Convertible()
    pdpScriptPolicyID_Script!: Script;

    @Convertible()
    pdpScriptPolicyID_Params!: object;

    @Convertible()
    pdpScriptValidator_AddressMainnet!: string;

    @Convertible()
    pdpScriptValidator_AddressTestnet!: string;

    @Convertible()
    pdpScriptValidator_Script!: Script;

    @Convertible()
    pdpScriptValidator_Hash!: string;

    @Convertible()
    pdpScriptValidator_Params!: object;

    @Convertible()
    pdpSwapOfferPolicyID_CS!: CS;

    @Convertible()
    pdpSwapOfferPolicyID_Script!: Script;

    @Convertible()
    pdpSwapOfferPolicyID_Params!: object;

    @Convertible()
    pdpSwapOfferValidator_AddressMainnet!: string;

    @Convertible()
    pdpSwapOfferValidator_AddressTestnet!: string;

    @Convertible()
    pdpSwapOfferValidator_Script!: Script;

    @Convertible()
    pdpSwapOfferValidator_Hash!: string;

    @Convertible()
    pdpSwapOfferValidator_Params!: object;

    @Convertible()
    pdpDelegationPolicyID_CS!: CS;

    @Convertible()
    pdpDelegationPolicyID_Script!: Script;

    @Convertible()
    pdpDelegationPolicyID_Params!: object;

    @Convertible()
    pdpDelegationValidator_AddressMainnet!: string;

    @Convertible()
    pdpDelegationValidator_AddressTestnet!: string;

    @Convertible()
    pdpDelegationValidator_Script!: Script;

    @Convertible()
    pdpDelegationValidator_Hash!: string;

    @Convertible()
    pdpDelegationValidator_Params!: object;

    @Convertible()
    pdpInvestUnitValidator_AddressMainnet!: string;

    @Convertible()
    pdpInvestUnitValidator_AddressTestnet!: string;

    @Convertible()
    pdpInvestUnitValidator_Script!: Script;

    @Convertible()
    pdpInvestUnitValidator_Hash!: string;

    @Convertible()
    pdpInvestUnitValidator_Params!: object;

    @Convertible()
    pdpBuyOrderPolicyID_CS!: CS;

    @Convertible()
    pdpBuyOrderPolicyID_Script!: Script;

    @Convertible()
    pdpBuyOrderPolicyID_Params!: object;

    @Convertible()
    pdpBuyOrderValidator_AddressMainnet!: string;

    @Convertible()
    pdpBuyOrderValidator_AddressTestnet!: string;

    @Convertible()
    pdpBuyOrderValidator_Script!: Script;

    @Convertible()
    pdpBuyOrderValidator_Hash!: string;

    @Convertible()
    pdpBuyOrderValidator_Params!: object;

    @Convertible({ type: Object })
    pdpFundFactories!: FundFactory[];

    @Convertible({ isCreatedAt: true })
    createdAt!: Date;

    @Convertible({ isUpdatedAt: true })
    updatedAt!: Date;

    // #endregion fields

    // #region datum

    @Convertible({ isForDatum: true })
    pdProtocolVersion!: number;

    @Convertible({ isForDatum: true, type: String })
    pdScriptPolicyID_CS!: CS;

    @Convertible({ isForDatum: true })
    pdScriptValidator_Hash!: string;

    @Convertible({ isForDatum: true })
    pdOraclePaymentPubKey!: PaymentPubKey;

    @Convertible({ isForDatum: true, type: BigInt })
    pdOracleData_Valid_Time!: POSIXTime;

    @Convertible({ isForDatum: true, type: String })
    pdAdmins!: PaymentKeyHash[];

    @Convertible({ isForDatum: true, type: String })
    pdDelegatorsAdmins!: PaymentKeyHash[];

    @Convertible({ isForDatum: true, type: String })
    pdTokenAdminPolicy_CS!: CS;

    @Convertible({
        isForDatum: true,
        type: Object,
        fromPlainObject: deserealizeFundCategory,
        fromPlutusData: fundCategoryfromPlutusData,
    })
    pdFundCategories!: FundCategory[];

    @Convertible({
        isForDatum: true,
        type: MinMaxDef<bigint>,
        fromPlainObject: MinMaxDef.fromPlainObject_BigInt,
    })
    pdFundLifeTime!: MinMaxDef<POSIXTime>;

    @Convertible({ isForDatum: true, type: AssetClass })
    pdTokenGov_AC!: AssetClass;

    @Convertible({ isForDatum: true })
    pdRequiredTokenGovForSwapOffer!: bigint;

    @Convertible({ isForDatum: true })
    pdRequiredTokenGovForBuyOrder!: bigint;

    @Convertible({ isForDatum: true, type: MinMaxDef<bigint>, fromPlainObject: MinMaxDef.fromPlainObject_BigInt })
    pdCommissionFund_PerYear_InBPx1e3!: MinMaxDef<bigint>;

    @Convertible({ isForDatum: true, type: MinMaxDef<bigint>, fromPlainObject: MinMaxDef.fromPlainObject_BigInt })
    pdCommissionSwapOffer_InBPx1e3!: MinMaxDef<bigint>;

    @Convertible({ isForDatum: true, type: MinMaxDef<bigint>, fromPlainObject: MinMaxDef.fromPlainObject_BigInt })
    pdCommissionBuyOrder_InBPx1e3!: MinMaxDef<bigint>;

    @Convertible({ isForDatum: true })
    pdShare_InBPx1e2_Protocol!: number;

    @Convertible({ isForDatum: true })
    pdShare_InBPx1e2_Managers!: number;

    @Convertible({ isForDatum: true })
    pdShare_InBPx1e2_Delegators!: number;

    @Convertible({ isForDatum: true })
    pdMaxDepositAndWithdraw!: bigint;

    @Convertible({ isForDatum: true })
    pdMinADA!: bigint;

    // #endregion datum

    // #region db

    public static defaultFieldsWhenUndefined: Record<string, boolean> = {
        pdpProtocolPolicyID_Script: false,
        pdpProtocolValidator_Script: false,
        pdpScriptPolicyID_Script: false,
        pdpScriptValidator_Script: false,
    };

    public static alwaysFieldsForSelect: Record<string, boolean> = {
        ...super.alwaysFieldsForSelect,
        name: true,
        funds_ids: true,
        pdpProtocolPolicyID_CS: true,
        //TODO: mejorar esto, por ahora nos e bien donde se usan asi que por las dudas
        pdTokenGov_AC: true,
        pdpProtocolValidator_AddressMainnet: true,
        pdpProtocolValidator_AddressTestnet: true,
        pdpScriptValidator_AddressMainnet: true,
        pdpScriptValidator_AddressTestnet: true,
        pdpSwapOfferValidator_AddressMainnet: true,
        pdpSwapOfferValidator_AddressTestnet: true,
        pdpBuyOrderValidator_AddressMainnet: true,
        pdpBuyOrderValidator_AddressTestnet: true,
        pdpDelegationValidator_AddressMainnet: true,
        pdpDelegationValidator_AddressTestnet: true,
        pdpInvestUnitValidator_AddressMainnet: true,
        pdpInvestUnitValidator_AddressTestnet: true,
    };

    public static defaultFieldsForAddresses: Record<string, boolean> = {
        pdpProtocolValidator_AddressMainnet: true,
        pdpProtocolValidator_AddressTestnet: true,
        pdpScriptValidator_AddressMainnet: true,
        pdpScriptValidator_AddressTestnet: true,
        pdpSwapOfferValidator_AddressMainnet: true,
        pdpSwapOfferValidator_AddressTestnet: true,
        pdpBuyOrderValidator_AddressMainnet: true,
        pdpBuyOrderValidator_AddressTestnet: true,
        pdpDelegationValidator_AddressMainnet: true,
        pdpDelegationValidator_AddressTestnet: true,
        pdpInvestUnitValidator_AddressMainnet: true,
        pdpInvestUnitValidator_AddressTestnet: true,
    };

    public static defaultFieldsAddScriptsTxScript: Record<string, boolean> = {
        pdpProtocolPolicyID_CS: true,
        pdpProtocolPolicyID_Script: true,
        pdpProtocolValidator_Hash: true,
        pdpProtocolValidator_Script: true,
        pdpScriptPolicyID_CS: true,
        pdpScriptPolicyID_Script: true,
        pdpScriptValidator_Hash: true,
        pdpScriptValidator_Script: true,
        pdpSwapOfferPolicyID_CS: true,
        pdpSwapOfferPolicyID_Script: true,
        pdpSwapOfferValidator_Hash: true,
        pdpSwapOfferValidator_Script: true,
        pdpDelegationPolicyID_CS: true,
        pdpDelegationPolicyID_Script: true,
        pdpDelegationValidator_Hash: true,
        pdpDelegationValidator_Script: true,
        pdpInvestUnitValidator_Hash: true,
        pdpInvestUnitValidator_Script: true,
    };

    public static defaultFieldsForProtocolScripts: Record<string, boolean> = {
        pdpProtocolPolicyID_CS: true,
        pdpProtocolPolicyID_Script: true,
        pdpProtocolPolicyID_Params: true,
        pdpProtocolValidator_AddressMainnet: true,
        pdpProtocolValidator_AddressTestnet: true,
        pdpProtocolValidator_Script: true,
        pdpProtocolValidator_Hash: true,
        pdpProtocolValidator_Params: true,
    };

    public static defaultFieldsForScriptScripts: Record<string, boolean> = {
        pdpScriptPolicyID_CS: true,
        pdpScriptPolicyID_Script: true,
        pdpScriptPolicyID_Params: true,
        pdpScriptValidator_AddressMainnet: true,
        pdpScriptValidator_AddressTestnet: true,
        pdpScriptValidator_Script: true,
        pdpScriptValidator_Hash: true,
        pdpScriptValidator_Params: true,
    };

    public static defaultFieldsForInvestUnitScripts: Record<string, boolean> = {
        pdpInvestUnitValidator_AddressMainnet: true,
        pdpInvestUnitValidator_AddressTestnet: true,
        pdpInvestUnitValidator_Script: true,
        pdpInvestUnitValidator_Hash: true,
        pdpInvestUnitValidator_Params: true,
    };

    public static defaultFieldsForBuyOrderScripts: Record<string, boolean> = {
        pdpBuyOrderPolicyID_CS: true,
        pdpBuyOrderPolicyID_Script: true,
        pdpBuyOrderPolicyID_Params: true,
        pdpBuyOrderValidator_AddressMainnet: true,
        pdpBuyOrderValidator_AddressTestnet: true,
        pdpBuyOrderValidator_Script: true,
        pdpBuyOrderValidator_Hash: true,
        pdpBuyOrderValidator_Params: true,
    };

    public static defaultFieldsForSwapOfferScripts: Record<string, boolean> = {
        pdpSwapOfferPolicyID_CS: true,
        pdpSwapOfferPolicyID_Script: true,
        pdpSwapOfferPolicyID_Params: true,
        pdpSwapOfferValidator_AddressMainnet: true,
        pdpSwapOfferValidator_AddressTestnet: true,
        pdpSwapOfferValidator_Script: true,
        pdpSwapOfferValidator_Hash: true,
        pdpSwapOfferValidator_Params: true,
    };

    public static defaultFieldsForDelegationScripts: Record<string, boolean> = {
        pdpDelegationPolicyID_CS: true,
        pdpDelegationPolicyID_Script: true,
        pdpDelegationPolicyID_Params: true,
        pdpDelegationValidator_AddressMainnet: true,
        pdpDelegationValidator_AddressTestnet: true,
        pdpDelegationValidator_Script: true,
        pdpDelegationValidator_Hash: true,
        pdpDelegationValidator_Params: true,
    };

    public static optionsGetForAppStoreProtocolLists: OptionsGet = {
        ...optionsGetMinimal,
        fieldsForSelect: {
            funds_ids: true,
            name: true,
            pdpProtocolVersion: true,
            pdProtocolVersion: true,
            pdpSwapOfferVersion: true,
            pdpBuyOrderVersion: true,
            pdpDelegationVersion: true,
            pdpScriptVersion: true,
            pdpProtocolPolicyID_CS: true,
            pdpFundFactories: true,
            // pdOraclePaymentPubKey: true,
            pdAdmins: true,
            pdDelegatorsAdmins: true,
            pdTokenAdminPolicy_CS: true,
            pdFundCategories: true,
            pdFundLifeTime: true,
            pdTokenGov_AC: true,
            pdRequiredTokenGovForSwapOffer: true,
            pdRequiredTokenGovForBuyOrder: true,
            pdCommissionFund_PerYear_InBPx1e3: true,
            pdCommissionSwapOffer_InBPx1e3: true,
            pdCommissionBuyOrder_InBPx1e3: true,
            pdShare_InBPx1e2_Protocol: true,
            pdShare_InBPx1e2_Managers: true,
            pdShare_InBPx1e2_Delegators: true,
            pdMaxDepositAndWithdraw: true,
            pdMinADA: true,
        },
    };

    // #endregion db

    // #region class methods

    public getNet_Address(): string {
        if (isMainnet) {
            return this.pdpProtocolValidator_AddressMainnet;
        } else {
            return this.pdpProtocolValidator_AddressTestnet;
        }
    }

    public getNET_id_CS(): string {
        return this.pdpProtocolPolicyID_CS;
    }

    public getNet_Script_Validator_Address(): string {
        if (isMainnet) {
            return this.pdpScriptValidator_AddressMainnet;
        } else {
            return this.pdpScriptValidator_AddressTestnet;
        }
    }

    public getNet_SwapOffer_Validator_Address(): string {
        if (isMainnet) {
            return this.pdpSwapOfferValidator_AddressMainnet;
        } else {
            return this.pdpSwapOfferValidator_AddressTestnet;
        }
    }

    public getNet_BuyOrder_Validator_Address(): string {
        if (isMainnet) {
            return this.pdpBuyOrderValidator_AddressMainnet;
        } else {
            return this.pdpBuyOrderValidator_AddressTestnet;
        }
    }

    public getNet_Delegation_Validator_Address(): string {
        if (isMainnet) {
            return this.pdpDelegationValidator_AddressMainnet;
        } else {
            return this.pdpDelegationValidator_AddressTestnet;
        }
    }

    public getNet_InvestUnit_Validator_Address(): string {
        if (isMainnet) {
            return this.pdpInvestUnitValidator_AddressMainnet;
        } else {
            return this.pdpInvestUnitValidator_AddressTestnet;
        }
    }

    public isAdmin(pkh: PaymentKeyHash): boolean {
        if (isNullOrBlank(pkh)) {
            throw `pkh not defined`;
        }
        return this.pdAdmins.includes(pkh);
    }

    // #endregion class methods
}
